import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Injectable, Injector, InputSignal, OnDestroy, OutputEmitterRef } from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { fromEvent, merge, startWith, takeUntil, tap } from 'rxjs';
import { PopoverContentComponent } from '../../components/popover-content/popover-content.component';
import { PopoverAttributes } from './popover.model';

export interface PopoverContentBase {
  htmlContent: InputSignal<string>;
  linkUrl: InputSignal<string | undefined>;
  isPinned: InputSignal<boolean>;
  allowEditing: InputSignal<boolean>;
  closePopover: OutputEmitterRef<void>;
  contentChanged: OutputEmitterRef<string>;
  linkUrlChanged: OutputEmitterRef<string>;
}

export interface PopoverServiceConfig {
  /** Whether the popover content can be edited. Default: false */
  allowEditing: boolean;
  /** Callback when content changes in edit mode. Receives the popover ID and new content. */
  onContentChange?: (popoverId: string, htmlContent: string) => void;
  /** Callback when link URL changes in edit mode. Receives the popover ID and new link URL. */
  onLinkUrlChange?: (popoverId: string, linkUrl: string) => void;
}

const DEFAULT_CONFIG: PopoverServiceConfig = {
  allowEditing: false,
};

const VIEWPORT_MARGIN_PX = 16;
const MIN_POPOVER_HEIGHT_PX = 192; // 12rem

@Injectable({
  providedIn: 'root',
})
export class PopoverService implements OnDestroy {
  constructor(
    private readonly overlay: Overlay,
    private readonly injector: Injector,
  ) {}

  private overlayRef: OverlayRef | null = null;
  private componentRef: ComponentRef<PopoverContentBase> | null = null;
  private hidePreviewTimeout: ReturnType<typeof setTimeout> | null = null;
  private isPinned = false;
  private currentTargetElement: HTMLElement | null = null;
  private currentPopoverId: string | null = null;

  config: PopoverServiceConfig = { ...DEFAULT_CONFIG };

  ngOnDestroy(): void {
    this.close();
  }

  showPreview(target: HTMLElement, attrs: { id?: string; htmlContent: string; linkUrl?: string }): void {
    this.clearHideTimeout();

    if (this.isPinned) {
      return;
    }

    if (this.overlayRef && this.currentTargetElement === target) {
      return;
    }

    if (this.overlayRef) {
      this.destroyOverlay();
    }

    this.currentTargetElement = target;
    this.currentPopoverId = attrs.id || null;
    this.createOverlay(target, attrs.htmlContent, attrs.linkUrl, false);
  }

  hidePreview(): void {
    if (this.isPinned) {
      return;
    }

    this.clearHideTimeout();

    this.hidePreviewTimeout = setTimeout(() => {
      if (!this.isPinned) {
        this.close();
      }
    }, 100);
  }

  pinPopover(target: HTMLElement, attrs: PopoverAttributes): void {
    this.clearHideTimeout();

    if (this.isPinned && this.currentTargetElement === target) {
      this.close();
      return;
    }

    this.destroyOverlay();

    this.currentTargetElement = target;
    this.currentPopoverId = attrs.id || null;
    this.isPinned = true;
    this.createOverlay(target, attrs.htmlContent, attrs.linkUrl, true);
  }

  close(): void {
    this.clearHideTimeout();
    this.isPinned = false;
    this.currentTargetElement = null;
    this.currentPopoverId = null;
    this.destroyOverlay();
  }

  private createOverlay(target: HTMLElement, htmlContent: string, linkUrl: string | undefined, pinned: boolean): void {
    const positions: ConnectedPosition[] = [
      { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8 },
      { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8 },
      { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -8 },
      { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 8 },
    ];

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(target)
      .withPositions(positions)
      .withFlexibleDimensions(false)
      .withPush(true)
      .withViewportMargin(VIEWPORT_MARGIN_PX)
      .withLockedPosition(false);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
      panelClass: 'popover-overlay-panel',
    });

    const portal = new ComponentPortal(PopoverContentComponent, null, this.injector);
    this.componentRef = this.overlayRef.attach(portal);

    this.componentRef.setInput('htmlContent', htmlContent);
    this.componentRef.setInput('linkUrl', linkUrl);
    this.componentRef.setInput('isPinned', pinned);
    this.componentRef.setInput('allowEditing', this.config.allowEditing);

    outputToObservable(this.componentRef.instance.closePopover)
      .pipe(
        tap(() => this.close()),
        takeUntil(this.overlayRef.detachments()),
      )
      .subscribe();

    if (this.config.onContentChange) {
      outputToObservable(this.componentRef.instance.contentChanged)
        .pipe(
          tap((newContent) => {
            if (this.currentPopoverId) {
              this.config.onContentChange?.(this.currentPopoverId, newContent);
            }
          }),
          takeUntil(this.overlayRef.detachments()),
        )
        .subscribe();
    }

    if (this.config.onLinkUrlChange) {
      outputToObservable(this.componentRef.instance.linkUrlChanged)
        .pipe(
          tap((newLinkUrl) => {
            if (this.currentPopoverId) {
              this.config.onLinkUrlChange?.(this.currentPopoverId, newLinkUrl);
            }
          }),
          takeUntil(this.overlayRef.detachments()),
        )
        .subscribe();
    }

    const overlayElement = this.overlayRef.overlayElement;

    merge(
      fromEvent(overlayElement, 'mouseenter').pipe(tap(() => this.handleOverlayMouseEnter())),
      fromEvent(overlayElement, 'mouseleave').pipe(tap(() => this.handleOverlayMouseLeave())),
    )
      .pipe(takeUntil(this.overlayRef.detachments()))
      .subscribe();

    // Update max-height when position changes or window resizes
    merge(positionStrategy.positionChanges, fromEvent(window, 'resize'))
      .pipe(
        startWith(null),
        tap(() => this.updatePopoverMaxHeight(target, overlayElement)),
        takeUntil(this.overlayRef.detachments()),
      )
      .subscribe();

    if (pinned) {
      this.setupClickOutsideHandler();
    }
  }

  private updatePopoverMaxHeight(target: HTMLElement, overlayElement: HTMLElement): void {
    const viewportHeight = window.innerHeight;
    const overlayRect = overlayElement.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    const isPopoverBelow = overlayRect.top > targetRect.bottom;
    const isPopoverAbove = overlayRect.bottom < targetRect.top;

    let maxHeight: number;

    if (isPopoverBelow) {
      maxHeight = viewportHeight - overlayRect.top - VIEWPORT_MARGIN_PX;
    } else if (isPopoverAbove) {
      maxHeight = overlayRect.bottom - VIEWPORT_MARGIN_PX;
    } else {
      // Horizontal positioning - use the smaller of available vertical space
      const spaceAbove = overlayRect.bottom - VIEWPORT_MARGIN_PX;
      const spaceBelow = viewportHeight - overlayRect.top - VIEWPORT_MARGIN_PX;
      maxHeight = Math.min(spaceAbove, spaceBelow);
    }

    maxHeight = Math.max(MIN_POPOVER_HEIGHT_PX, maxHeight);

    overlayElement.style.setProperty('--popover-max-height', `${maxHeight}px`);
  }

  private readonly handleOverlayMouseEnter = (): void => {
    this.clearHideTimeout();
  };

  private readonly handleOverlayMouseLeave = (): void => {
    if (!this.isPinned) {
      this.hidePreview();
    }
  };

  private setupClickOutsideHandler(): void {
    if (!this.overlayRef) {
      return;
    }

    fromEvent<MouseEvent>(document, 'click')
      .pipe(
        tap((event) => this.handleDocumentClick(event)),
        takeUntil(this.overlayRef.detachments()),
      )
      .subscribe();

    fromEvent<KeyboardEvent>(document, 'keydown')
      .pipe(
        tap((event) => this.handleEscapeKey(event)),
        takeUntil(this.overlayRef.detachments()),
      )
      .subscribe();
  }

  private readonly handleDocumentClick = (event: Event): void => {
    if (!this.isPinned || !this.overlayRef) {
      return;
    }

    const clickedElement = event.target as Node;
    const clickedInsidePopover = this.overlayRef.overlayElement.contains(clickedElement);
    const clickedInsideTarget = this.currentTargetElement?.contains(clickedElement);

    if (!clickedInsidePopover && !clickedInsideTarget) {
      this.close();
    }
  };

  private readonly handleEscapeKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.isPinned) {
      this.close();
    }
  };

  private destroyOverlay(): void {
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef.dispose();
      this.overlayRef = null;
    }

    this.componentRef = null;
  }

  private clearHideTimeout(): void {
    if (this.hidePreviewTimeout) {
      clearTimeout(this.hidePreviewTimeout);
      this.hidePreviewTimeout = null;
    }
  }
}
